import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import CustomerHeader from '../../components/CustomerHeader';
import CustomerFooter from '../../components/CustomerFooter';
import ProductCard from '../../components/ProductCard';
import HeroCollage from '../../components/HeroCollage';
import HeroMobileSlider from '../../components/HeroMobileSlider';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { LoadingBlock } from '../../components/States';
import { ArrowRight, Leaf, Flower, Shovel, HeartHandshake, Sprout } from 'lucide-react';

const SERVICES = [
    { icon: Leaf, title: 'Landscaping Services', desc: 'Bespoke landscape design for homes and villas' },
    { icon: Sprout, title: 'Terrace Garden Setup', desc: 'Turn your terrace into an urban oasis' },
    { icon: Flower, title: 'Vertical Garden Setup', desc: 'Space-saving living walls that stun' },
    { icon: Shovel, title: 'Garden Maintenance', desc: 'Weekly, monthly and quarterly care plans' },
    { icon: HeartHandshake, title: 'Corporate Landscaping', desc: 'Green offices, healthier teams' },
];

// -------- Hero (uses CMS hero_slides) --------
function HomeHero({ slides }) {
    const first = (slides || []).find(s => s.active !== false) || null;
    return (
        <section className="relative overflow-hidden">
            <div className="absolute inset-0 login-hero-bg" />
            <div className="relative">
                {/* Desktop */}
                <div className="hidden lg:block max-w-[1400px] mx-auto px-6 py-16">
                    <div className="grid grid-cols-12 gap-8 items-center text-white">
                        <div className="col-span-7 max-w-2xl">
                            {first?.badge_text && (
                                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1 rounded-full text-xs font-medium mb-4">
                                    <Sprout className="h-3.5 w-3.5" /> {first.badge_text}
                                </div>
                            )}
                            <h1 className="text-5xl md:text-6xl font-extrabold tracking-tight leading-tight">
                                {first?.heading_line1 || 'Bring nature'}
                                {first?.heading_line2 && (<><br /><span style={{ color: first.highlight_color || 'hsl(142 76% 70%)' }}>{first.heading_line2}</span></>)}
                            </h1>
                            {first?.subheading && <p className="mt-4 text-lg text-white/85 max-w-lg">{first.subheading}</p>}
                            <div className="mt-8 flex flex-wrap gap-3">
                                {first?.cta_primary_label && first?.cta_primary_link && (
                                    <Link to={first.cta_primary_link}>
                                        <Button size="lg" data-testid="hero-shop-cta" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>
                                            {first.cta_primary_label} <ArrowRight className="h-4 w-4 ml-2" />
                                        </Button>
                                    </Link>
                                )}
                                {first?.cta_secondary_label && first?.cta_secondary_link && (
                                    <Link to={first.cta_secondary_link}>
                                        <Button size="lg" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white">
                                            {first.cta_secondary_label}
                                        </Button>
                                    </Link>
                                )}
                            </div>
                        </div>
                        <div className="col-span-5 flex justify-center">
                            {first?.desktop_image_url ? (
                                <div className="w-full max-w-[460px] rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20" data-testid="hero-desktop-image">
                                    <img
                                        src={first.desktop_image_url}
                                        alt={first.heading_line1 || ''}
                                        className="w-full h-[420px] object-cover"
                                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                                    />
                                </div>
                            ) : (
                                <HeroCollage />
                            )}
                        </div>
                    </div>
                </div>
                {/* Mobile / Tablet (auto slider) */}
                <div className="lg:hidden max-w-2xl mx-auto">
                    <HeroMobileSlider slides={slides || []} />
                </div>
            </div>
        </section>
    );
}

// -------- Categories section --------
function CategoriesSection({ section }) {
    const cats = (section.categories || []).filter(c => c && (c.active !== false));
    if (cats.length === 0) return null;
    return (
        <section className="py-10 sm:py-16">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                <div className="flex items-end justify-between mb-5 sm:mb-6">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{section.title}</h2>
                        {section.subtitle && <p className="text-neutral-600 mt-1 text-sm sm:text-base">{section.subtitle}</p>}
                    </div>
                    {section.view_all_link && (
                        <Link to={section.view_all_link} className="hidden sm:inline text-sm font-medium text-blue-700 hover:underline">Browse all →</Link>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="category-grid">
                    {cats.map(cat => {
                        const imgSrc = cat.image || 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=800&q=80';
                        return (
                            <Link key={cat.id} to={`/category/${cat.slug}`} className="group" data-testid={`home-cat-${cat.slug}`}>
                                <div className="relative aspect-[4/5] sm:aspect-[4/3] rounded-2xl overflow-hidden bg-neutral-100 shadow-sm hover:shadow-lg transition-shadow">
                                    <img src={imgSrc} alt={cat.name}
                                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        loading="lazy"
                                        onError={(e) => { e.currentTarget.src = 'https://images.unsplash.com/photo-1466692476868-aef1dfb1e735?w=800&q=80'; }} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                                    <div className="absolute bottom-3 left-3 right-3 text-white">
                                        <div className="font-semibold text-base sm:text-lg leading-tight">{cat.name}</div>
                                        <div className="text-[11px] sm:text-xs opacity-90 flex items-center gap-1 mt-1">
                                            Shop now <ArrowRight className="h-3 w-3" />
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
                {section.view_all_link && (
                    <div className="sm:hidden mt-4 text-center">
                        <Link to={section.view_all_link} className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700">Browse all →</Link>
                    </div>
                )}
            </div>
        </section>
    );
}

// -------- Products section --------
function ProductsSection({ section }) {
    const products = section.products || [];
    if (products.length === 0) return null;
    return (
        <section className="py-10 sm:py-16 bg-neutral-50" data-testid={`section-products-${section.id}`}>
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                <div className="flex items-end justify-between mb-5 sm:mb-6">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{section.title}</h2>
                        {section.subtitle && <p className="text-neutral-600 mt-1 text-sm sm:text-base">{section.subtitle}</p>}
                    </div>
                    {section.view_all_link && (
                        <Link to={section.view_all_link} className="text-sm font-medium text-blue-700 hover:underline">See all →</Link>
                    )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4" data-testid={`section-grid-${section.id}`}>
                    {products.map(p => <ProductCard key={p.id} product={p} />)}
                </div>
            </div>
        </section>
    );
}

// -------- Services (static block, always shown at bottom) --------
function ServicesBlock() {
    return (
        <section className="py-10 sm:py-16" id="services">
            <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
                <div className="flex items-end justify-between mb-5 sm:mb-6">
                    <div>
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">Our Landscaping &amp; Garden Services</h2>
                        <p className="text-neutral-600 mt-1 text-sm sm:text-base">From concept to lifelong maintenance</p>
                    </div>
                    <Link to="/services" className="hidden sm:inline text-sm font-medium text-blue-700 hover:underline">All services →</Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {SERVICES.map(s => (
                        <Card key={s.title} className="p-5 border hover:shadow-md transition-shadow">
                            <div className="h-10 w-10 rounded-lg flex items-center justify-center mb-3" style={{ background: 'hsl(142 76% 92%)' }}>
                                <s.icon className="h-5 w-5" style={{ color: 'hsl(142 71% 30%)' }} />
                            </div>
                            <div className="font-semibold">{s.title}</div>
                            <div className="text-sm text-neutral-600 mt-1">{s.desc}</div>
                        </Card>
                    ))}
                </div>
                <div className="mt-8">
                    <ServiceEnquiryTeaser />
                </div>
            </div>
        </section>
    );
}

// -------- Page --------
export default function Home() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try { const r = await api.get('/home/public'); setCfg(r.data); }
            catch { setCfg({ hero_slides: [], sections: [] }); }
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div className="bg-white min-h-screen">
            <CustomerHeader />
            {loading || !cfg ? (
                <div className="py-24"><LoadingBlock /></div>
            ) : (
                <>
                    <HomeHero slides={cfg.hero_slides || []} />
                    {(cfg.sections || []).map(sec => (
                        sec.type === 'categories'
                            ? <CategoriesSection key={sec.id} section={sec} />
                            : <ProductsSection key={sec.id} section={sec} />
                    ))}
                    <ServicesBlock />
                </>
            )}
            <CustomerFooter />
        </div>
    );
}

function ServiceEnquiryTeaser() {
    const [name, setName] = useState('');
    const [mobile, setMobile] = useState('');
    const [service, setService] = useState('Landscaping Services');
    const [message, setMessage] = useState('');
    const [sending, setSending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !mobile.trim()) { return; }
        setSending(true);
        try {
            await api.post('/service-enquiries', { name, mobile, service_type: service, message });
            setName(''); setMobile(''); setMessage('');
            const { toast } = await import('sonner');
            toast.success('Thanks! Our team will contact you within 24 hours.');
        } finally { setSending(false); }
    };

    return (
        <div className="rounded-2xl p-6 sm:p-8 bg-gradient-to-br from-blue-900 to-green-700 text-white" data-testid="home-enquiry-teaser">
            <div className="grid md:grid-cols-2 gap-6 items-center">
                <div>
                    <h3 className="text-2xl sm:text-3xl font-bold">Request a free consultation</h3>
                    <p className="text-white/85 mt-2 max-w-md">Tell us about your space and vision. Our team will get in touch within 24 hours with tailored ideas and pricing.</p>
                </div>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input required value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/60 px-3 py-2 text-sm outline-none" data-testid="teaser-name" />
                    <input required value={mobile} onChange={e => setMobile(e.target.value)} placeholder="Mobile" className="rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/60 px-3 py-2 text-sm outline-none" data-testid="teaser-mobile" />
                    <select value={service} onChange={e => setService(e.target.value)} className="rounded-md bg-white/10 border border-white/20 text-white px-3 py-2 text-sm outline-none sm:col-span-2" data-testid="teaser-service">
                        {SERVICES.map(s => <option key={s.title} value={s.title} className="text-neutral-900">{s.title}</option>)}
                    </select>
                    <textarea rows={2} value={message} onChange={e => setMessage(e.target.value)} placeholder="Message (optional)" className="rounded-md bg-white/10 border border-white/20 text-white placeholder:text-white/60 px-3 py-2 text-sm outline-none sm:col-span-2" />
                    <Button type="submit" disabled={sending} className="sm:col-span-2" data-testid="teaser-submit" style={{ background: 'hsl(142 71% 45%)', color: 'white' }}>{sending ? 'Sending...' : 'Request Consultation'}</Button>
                </form>
            </div>
        </div>
    );
}
